import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, X, Clock, Megaphone, ExternalLink } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

import { getTimeAgo } from '../lib/dateUtils';

// Announcement Card Component
const AnnouncementCard = ({ announcement, onClick }) => {
  return (
    <div
      onClick={() => onClick(announcement)}
      className="flex-shrink-0 w-[200px] sm:w-[220px] bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-lg border border-emerald-200 dark:border-emerald-800 overflow-hidden cursor-pointer group hover:shadow-md hover:border-emerald-400 transition-all"
    >
      {/* Image */}
      {announcement.image_url && (
        <div className="h-20 sm:h-24 overflow-hidden">
          <img
            src={announcement.image_url}
            alt={announcement.title || 'Announcement image'}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            onError={(e) => {
              e.target.src = 'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=400&h=200&fit=crop';
            }}
          />
        </div>
      )}
      
      {/* Content */}
      <div className="p-2 sm:p-2.5">
        {/* Badge */}
        <div className="flex items-center gap-1.5 mb-1">
          <span className={`px-1.5 py-0.5 text-[9px] sm:text-[10px] font-medium rounded-full ${
            announcement.type === 'ad' 
              ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
              : 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400'
          }`}>
            {announcement.type === 'ad' ? '📢 Ad' : '📣 News'}
          </span>
          <span className="text-[9px] sm:text-[10px] text-gray-500 flex items-center gap-0.5">
            <Clock className="h-2.5 w-2.5" />
            {getTimeAgo(announcement.created_at)}
          </span>
        </div>
        
        {/* Title */}
        <h3 className="font-semibold text-gray-900 dark:text-white text-xs sm:text-sm line-clamp-2 group-hover:text-emerald-600 transition-colors mb-0.5">
          {announcement.title}
        </h3>
        
        {/* Preview */}
        <p className="text-[10px] sm:text-xs text-gray-600 dark:text-gray-400 line-clamp-1">
          {announcement.preview || announcement.content?.substring(0, 60)}
        </p>
        
        {/* Partner/Source */}
        {announcement.partner && (
          <div className="mt-1.5 pt-1.5 border-t border-emerald-100 dark:border-emerald-800">
            <span className="text-[9px] sm:text-[10px] text-emerald-600 dark:text-emerald-400 font-medium">
              By {announcement.partner}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

// Related Announcement Card (smaller, for "More" section)
const RelatedAnnouncementCard = ({ announcement, onClick }) => {
  return (
    <div
      onClick={() => onClick(announcement)}
      className="flex gap-3 p-3 bg-emerald-50/50 dark:bg-emerald-900/10 rounded-xl hover:bg-emerald-100/50 dark:hover:bg-emerald-900/20 cursor-pointer transition-all group"
    >
      {/* Thumbnail */}
      {announcement.image_url && (
        <div className="w-16 h-14 sm:w-20 sm:h-16 flex-shrink-0 rounded-lg overflow-hidden">
          <img
            src={announcement.image_url}
            alt={announcement.title || 'Announcement thumbnail'}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            onError={(e) => {
              e.target.src = 'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=150&h=100&fit=crop';
            }}
          />
        </div>
      )}
      
      {/* Content */}
      <div className="flex-1 min-w-0">
        <span className={`inline-block px-1.5 py-0.5 text-[9px] font-medium rounded mb-1 ${
          announcement.type === 'ad' 
            ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
            : 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400'
        }`}>
          {announcement.type === 'ad' ? 'Sponsored' : 'Announcement'}
        </span>
        <h4 className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white group-hover:text-emerald-600 transition-colors line-clamp-2">
          {announcement.title}
        </h4>
        <span className="text-[10px] text-gray-500 mt-1 block">
          {getTimeAgo(announcement.created_at)}
        </span>
      </div>
    </div>
  );
};

// Announcement Reader Panel
const AnnouncementReaderPanel = ({ announcement, isOpen, onClose, allAnnouncements = [] }) => {
  // Derive related announcements directly instead of using setState in effect
  const relatedAnnouncements = React.useMemo(() => {
    if (!isOpen || !announcement || allAnnouncements.length === 0) {
      return [];
    }
    return allAnnouncements
      .filter(a => a.id !== announcement.id)
      .slice(0, 4);
  }, [isOpen, announcement, allAnnouncements]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen || !announcement) return null;

  const handleRelatedClick = (related) => {
    // Emit event to switch announcement
    window.dispatchEvent(new CustomEvent('switchAnnouncement', { detail: related }));
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-40 transition-opacity duration-300"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="fixed top-0 right-0 h-full w-full max-w-2xl bg-white dark:bg-gray-900 shadow-2xl z-50 flex flex-col transform transition-transform duration-300 ease-out">
        {/* Swipe indicators */}
        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-20 bg-emerald-400 rounded-r-full sm:hidden z-20 opacity-50" />
        
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center text-white">
              <Megaphone className="h-5 w-5" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                {announcement.partner || 'YEGO'}
              </p>
              <span className={`inline-block px-2 py-0.5 text-[10px] font-medium rounded-full ${
                announcement.type === 'ad' 
                  ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
                  : 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400'
              }`}>
                {announcement.type === 'ad' ? 'Sponsored' : 'Announcement'}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full"
          >
            <X className="h-6 w-6 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto">
          <div className="p-6">
            {/* Hero Image */}
            {announcement.image_url && (
              <div className="mb-6 rounded-xl overflow-hidden">
                <img
                  src={announcement.image_url}
                  alt=""
                  className="w-full max-h-[300px] object-cover"
                  onError={(e) => {
                    e.target.src = 'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=800&h=400&fit=crop';
                  }}
                />
              </div>
            )}

            {/* Title */}
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-4">
              {announcement.title}
            </h1>

            {/* Meta */}
            <div className="flex items-center gap-4 mb-6 text-sm text-gray-500">
              <span className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                {getTimeAgo(announcement.created_at)}
              </span>
              {announcement.link && (
                <a 
                  href={announcement.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-emerald-500 hover:underline"
                >
                  <ExternalLink className="h-4 w-4" />
                  Learn more
                </a>
              )}
            </div>

            {/* Content */}
            <div className="prose dark:prose-invert max-w-none">
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">
                {announcement.content}
              </p>
            </div>

            {/* CTA Button */}
            {announcement.cta_text && announcement.link && (
              <div className="mt-8">
                <a
                  href={announcement.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-medium rounded-full hover:from-emerald-600 hover:to-teal-600 transition-all"
                >
                  {announcement.cta_text}
                  <ExternalLink className="h-4 w-4" />
                </a>
              </div>
            )}

            {/* More Announcements Section */}
            {relatedAnnouncements.length > 0 && (
              <div className="mt-10 pt-6 border-t border-gray-200 dark:border-gray-800">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <span className="w-1 h-5 bg-emerald-500 rounded-full"></span>
                  More Announcements
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {relatedAnnouncements.map((related) => (
                    <RelatedAnnouncementCard
                      key={related.id}
                      announcement={related}
                      onClick={handleRelatedClick}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

// Main Announcements Section Component
const AnnouncementsSection = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [readerOpen, setReaderOpen] = useState(false);
  const scrollRef = useRef(null);

  // Fetch announcements
  useEffect(() => {
    const fetchAnnouncements = async () => {
      setLoading(true);
      
      // Sample data for when database is empty
      const sampleAnnouncements = [
        {
          id: '1',
          title: 'Welcome to YEGO - Your Trusted Source for Rwanda News',
          preview: 'Discover the latest news, stories, and updates from Rwanda and around the world.',
          content: 'Welcome to YEGO! We are dedicated to bringing you the most accurate and up-to-date news from Rwanda and the world. Our team of journalists works tirelessly to ensure you stay informed about the events that matter most.\n\nJoin our community of readers and be part of the conversation. Subscribe to our newsletter for daily updates delivered straight to your inbox.',
          image_url: 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=600&h=300&fit=crop',
          type: 'announcement',
          partner: 'YEGO',
          created_at: new Date().toISOString(),
          cta_text: 'Subscribe Now',
          link: '#'
        },
        {
          id: '2',
          title: 'Partner With Us - Reach Thousands of Engaged Readers',
          preview: 'Advertise your business to our growing audience of news enthusiasts.',
          content: 'Looking to grow your business? Partner with YEGO and reach thousands of engaged readers daily. Our advertising solutions are designed to help you connect with your target audience effectively.\n\nWe offer various advertising options including sponsored content, display ads, and featured listings. Contact us today to learn more about our partnership opportunities.',
          image_url: 'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=600&h=300&fit=crop',
          type: 'ad',
          partner: 'YEGO Ads',
          created_at: new Date(Date.now() - 86400000).toISOString(),
          cta_text: 'Contact Us',
          link: '#'
        },
        {
          id: '3',
          title: 'New Feature: Save Articles to Your Library',
          preview: 'Never lose track of important articles again with our new Library feature.',
          content: 'We are excited to announce our new Library feature! Now you can save any article to read later. Simply right-click on any article and select "Save to Library".\n\nYour saved articles are always accessible from the Library tab in the navigation menu. Start building your personal collection of must-read articles today!',
          image_url: 'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=600&h=300&fit=crop',
          type: 'announcement',
          partner: 'YEGO',
          created_at: new Date(Date.now() - 172800000).toISOString()
        }
      ];
      
      try {
        const response = await axios.get(`${BACKEND_URL}/api/announcements`);
        const data = response.data || [];
        // Use sample data if API returns empty array
        setAnnouncements(data.length > 0 ? data : sampleAnnouncements);
      } catch (error) {
        console.error('Failed to fetch announcements:', error);
        // Use sample data if API fails
        setAnnouncements(sampleAnnouncements);
      } finally {
        setLoading(false);
      }
    };

    fetchAnnouncements();
  }, []);

  // Listen for announcement switching
  useEffect(() => {
    const handleSwitch = (event) => {
      setSelectedAnnouncement(event.detail);
    };
    window.addEventListener('switchAnnouncement', handleSwitch);
    return () => window.removeEventListener('switchAnnouncement', handleSwitch);
  }, []);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 320;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const openAnnouncement = (announcement) => {
    setSelectedAnnouncement(announcement);
    setReaderOpen(true);
  };

  if (loading) {
    return (
      <section className="mb-6 sm:mb-8">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-5 h-5 rounded-full bg-emerald-500/20 animate-pulse" />
          <div className="h-4 w-36 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
        </div>
        <div className="flex gap-3 overflow-hidden">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="w-[200px] h-[140px] bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse flex-shrink-0" />
          ))}
        </div>
      </section>
    );
  }

  if (announcements.length === 0) return null;

  return (
    <>
      <section className="mb-6 sm:mb-8">
        {/* Header - Perfectly aligned with flexbox */}
        <div className="flex items-center justify-between mb-3 sm:mb-4">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center flex-shrink-0">
              <Megaphone className="h-2.5 w-2.5 sm:h-3 sm:w-3 text-white" />
            </div>
            <h2 className="text-sm sm:text-xl font-semibold text-gray-900 dark:text-white leading-none whitespace-nowrap">
              YEGO Ads & Partners
            </h2>
          </div>
          
          {/* Scroll Buttons - Desktop only */}
          <div className="hidden sm:flex items-center gap-2 flex-shrink-0 ml-2">
            <button
              onClick={() => scroll('left')}
              className="p-2 rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            >
              <ChevronLeft className="h-4 w-4 text-gray-600 dark:text-gray-400" />
            </button>
            <button
              onClick={() => scroll('right')}
              className="p-2 rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            >
              <ChevronRight className="h-4 w-4 text-gray-600 dark:text-gray-400" />
            </button>
          </div>
        </div>

        {/* Scrollable Grid */}
        <div className="relative">
          {/* Gradient hints for scroll on mobile */}
          <div className="absolute left-0 top-0 bottom-0 w-4 bg-gradient-to-r from-gray-50 dark:from-gray-950 to-transparent pointer-events-none z-10 sm:hidden" />
          <div className="absolute right-0 top-0 bottom-0 w-4 bg-gradient-to-l from-gray-50 dark:from-gray-950 to-transparent pointer-events-none z-10 sm:hidden" />
          
          <div
            ref={scrollRef}
            className="flex gap-3 sm:gap-4 overflow-x-auto scrollbar-hide pb-2 -mx-3 px-3 sm:mx-0 sm:px-0"
            style={{ WebkitOverflowScrolling: 'touch' }}
            onTouchStart={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
            onTouchEnd={(e) => e.stopPropagation()}
          >
            {announcements.map((announcement) => (
              <AnnouncementCard
                key={announcement.id}
                announcement={announcement}
                onClick={openAnnouncement}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Announcement Reader Panel */}
      <AnnouncementReaderPanel
        announcement={selectedAnnouncement}
        isOpen={readerOpen}
        onClose={() => setReaderOpen(false)}
        allAnnouncements={announcements}
      />
    </>
  );
};

export default AnnouncementsSection;
